import { domReady, queue } from '.';
import autocomplete, { AutocompleteItem } from 'autocompleter';
import SlimSelect, { Option } from 'slim-select';
import { getDefaultLang, getMessage } from '../i18n';
import './searchOffers.css';
import { CcDefaultConfig } from '../shared';
import { getPreferredLabel } from '../geocoder';

export interface CcSearchOffersConfig extends CcDefaultConfig {
  lang: string
  job: {
    display: boolean,
    disableAutocomplete: boolean,
    defaultValue: string,
    placeholder: string,
    classes: string,
    title: string,
    roundedDefault: boolean,
  },
  location: {
    display: boolean,
    searchByGeonameId: boolean,
    defaultValue: string,
    placeholder: string,
    classes: string,
    title: string,
    roundedDefault: boolean,
  },
  contractType: {
    display: boolean,
    placeholder: string,
    classes: string,
    title: string,
    roundedDefault: boolean,
    noResult: string,
  },
  industries: Array<string>,
  button: {
    displayIcon: boolean,
    displayText: boolean,
    text: string,
    color: string,
    bgColor: string,
    classes: string,
    roundedDefault: boolean,
  },
  block: {
    font: string,
    color: string,
    displayStyle: number,
  }
  newTab: boolean,
}

export const ccSearchOffersDefaultConfig: CcSearchOffersConfig = {
  lang: getDefaultLang(),
  domain: '',
  job: {
    display: true,
    disableAutocomplete: false,
    defaultValue: '',
    placeholder: getMessage(getDefaultLang(), 'searchOffers.job.placeholder'),
    classes: '',
    title: getMessage(getDefaultLang(), 'searchOffers.job.title'),
    roundedDefault: true,
  },
  location: {
    display: true,
    searchByGeonameId: false,
    defaultValue: '',
    placeholder: getMessage(getDefaultLang(), 'searchOffers.location.placeholder'),
    classes: '',
    title: getMessage(getDefaultLang(), 'searchOffers.location.title'),
    roundedDefault: true,
  },
  contractType: {
    display: true,
    placeholder: getMessage(getDefaultLang(), 'searchOffers.contractType.placeholder'),
    classes: '',
    title: getMessage(getDefaultLang(), 'searchOffers.contractType.title'),
    roundedDefault: true,
    noResult: getMessage(getDefaultLang(), 'searchOffers.contractType.noResult'),
  },
  industries: [],
  button: {
    displayIcon: true,
    displayText: true,
    text: getMessage(getDefaultLang(), 'searchOffers.button.text'),
    color: '',
    bgColor: '',
    classes: '',
    roundedDefault: true,
  },
  block: {
    font: '',
    color: '',
    displayStyle: 1,
  },
  newTab: false,
} as CcSearchOffersConfig;

interface Suggestions {
  interpretations: {
    what: string,
    where: string,
    feature: {
      name: string,
      highlightedName: string,
      id: string,
      [key: string]: any
    },
    parents: any
  }[];
}

interface LocationAutocompleteItem extends AutocompleteItem {
  value: string;
  name: string;
  preferredLabel: string;
}

function isSearchBtnEnabled(jobInput: HTMLInputElement, locationInput: HTMLInputElement, displaySelect: null | SlimSelect, config: CcSearchOffersConfig) {
  return jobInput?.value || locationInput?.value || displaySelect?.getSelected().length || config.job?.defaultValue || config.location?.defaultValue;
}

function display(elementId: string, config: CcSearchOffersConfig): string {
  const color = (color: string) => `color:${color}`;
  const bgColor = (bgColor: string) => `background-color:${bgColor}`;
  const font: (font: string) => string = (font: string) => `font-family:${font}`;
  const blockStyles: string[] = [];
  config.block.font && blockStyles.push(font(config.block.font));
  config.block.color && blockStyles.push(color(config.block.color));
  const buttonStyles: string[] = [];
  config.button.color && buttonStyles.push(color(config.button.color));
  config.button.bgColor && buttonStyles.push(bgColor(config.button.bgColor));

  let job = '';
  let location = '';
  let contractType = '';
  let searchBtn = '';

  const icon = config.button.displayIcon ? `<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" data-prefix="fas" data-icon="search" class="svg-inline--fa fa-search fa-w-16" role="img" viewBox="0 0 512 512"
  height="14px" width="14px">
    <path fill="${config.button.color ? config.button.color : 'currentColor'}" d="M505 442.7L405.3 343c-4.5-4.5-10.6-7-17-7H372c27.6-35.3 44-79.7 44-128C416 93.1 322.9 0 208 0S0 93.1 0 208s93.1 208 208 208c48.3 0 92.7-16.4 128-44v16.3c0 6.4 2.5 12.5 7 17l99.7 99.7c9.4 9.4 24.6 9.4 33.9 0l28.3-28.3c9.4-9.4 9.4-24.6.1-34zM208 336c-70.7 0-128-57.2-128-128 0-70.7 57.2-128 128-128 70.7 0 128 57.2 128 128 0 70.7-57.2 128-128 128z"/>
  </svg>` : '';
  const textButton = config.button.displayText ? config.button.text : `<span class="cc-sr-only">${getMessage(config.lang, 'searchOffers.button.text')}</span>`;

  const jobClass = config.job.classes ? config.job.classes : '';
  const locationClass = config.location.classes ? config.location.classes : '';
  const contractClass = config.contractType.classes ? config.contractType.classes : '';
  const searchClass = config.button.classes ? config.button.classes : '';

  const jobAnnouncement = config.job.disableAutocomplete
    ? ''
    : `<div class="cc-sr-only" aria-live="polite"><p id="cc-job-announcement"></p></div>`;

  const locationAnnouncement = `<div class="cc-sr-only" aria-live="polite"><p id="cc-location-announcement"></p></div>`;

  const contractTypeAnnouncement = `<div class="cc-sr-only" aria-live="polite"><p id="cc-contract-type-announcement"></p></div>`;

  if (config.block.displayStyle == 1) {

    job = config.job.display
      ? `<div style="flex: 1; vertical-align: top;" class="cc-column">
            ${jobAnnouncement}
            <input type="text"
                   class="cc-input-jobs cc-input ${jobClass} ${config.job.roundedDefault ? 'roundedDefault' : ''}"
                   title="${config.job.title}"
                   aria-label="${config.job.title}"
                   placeholder="${config.job.placeholder}">
         </div>`
      : '';

    location = config.location.display
      ? `<div style="flex: 1; vertical-align: top;" class="cc-column">
            ${locationAnnouncement}
            <input type="text"
                   class="cc-input-locations cc-input ${locationClass} ${config.location.roundedDefault ? 'roundedDefault' : ''}"
                   title="${config.location.title}"
                   aria-label="${config.location.title}"
                   placeholder="${config.location.placeholder}">
         </div>`
      : '';

    contractType = config.contractType.display
      ? `<div style="flex: 1; vertical-align: top;" class="cc-column">
            ${contractTypeAnnouncement}
            <select multiple
                    class="cc-input-contract-types ${contractClass} ${config.contractType.roundedDefault ? 'roundedDefault' : ''}"
                    title="${config.contractType.title}"
                    aria-label="${config.contractType.title}"></select>
         </div>`
      : '';

    searchBtn = `<div style="vertical-align: top; padding: 0 5px;">
    <button class="submit-btn cc-search-btn ${searchClass} ${config.button.roundedDefault ? 'roundedDefault' : ''}" type="button" style="margin: 0px auto; border-style: none; padding: 0.5rem 2rem; ${buttonStyles.join(';')}">
        ${icon}
        ${textButton}
      </button>
    </div>`;

    return `
    <form id="cc-${elementId}-form" method="POST" action="" style="border-radius: 1rem;${blockStyles.join(';')}">
      <fieldset>
        <legend class="cc-sr-only">${getMessage(config.lang, 'searchOffers.form.legend')}</legend>
        <div class="cc-div">
          <div class="cc-div1">
            ${job}
            ${location}
            ${contractType}
          </div>
          <div class="cc-div2">
            ${searchBtn}
          </div>
        </div>
      </fieldset>
    </form>
  `;

  } else {
    job = config.job.display
      ? `<div style="vertical-align: top; margin: auto;" class="cc-column">
            ${jobAnnouncement}
            <label for="job-input" style="margin-top: 10px;">${getMessage(config.lang, 'searchOffers.job.label')} :</label>
            <input id="job-input" type="text"
                   class="cc-input-jobs cc-input ${jobClass} ${config.job.roundedDefault ? 'roundedDefault' : ''}"
                   title="${config.job.title}"
                   aria-label="${config.job.title}"
                   placeholder="${config.job.placeholder}">
         </div>`
      : '';

    location = config.location.display
      ? `<div style="margin-top: 10px; vertical-align: top; margin: auto;" class="cc-column">
            ${locationAnnouncement}
            <label for="location-input" style="margin-top: 10px;">${getMessage(config.lang, 'searchOffers.location.label')} :</label>
            <input id="location-input"
                   type="text"
                   class="cc-input-locations cc-input ${locationClass} ${config.location.roundedDefault ? 'roundedDefault' : ''}"
                   title="${config.location.title}"
                   aria-label="${config.location.title}"
                   placeholder="${config.location.placeholder}">
         </div>`
      : '';

    contractType = config.contractType.display
      ? `<div style="padding-top: 10px; vertical-align: top; margin: auto;" class="cc-column">
            ${contractTypeAnnouncement}
            <label for="contract-type-select">${getMessage(config.lang, 'searchOffers.contractType.label')} :</label>
            <select id="contract-type-select"
                    multiple
                    class="cc-input-contract-types ${contractClass} ${config.contractType.roundedDefault ? 'roundedDefault' : ''}"
                    title="${config.contractType.title}"
                    aria-label="${config.contractType.title}"></select>
         </div>`
      : '';

    searchBtn = `<div style="padding-top: 10px; vertical-align: top; margin: auto;" class="cc-column">
    <button class="submit-btn cc-search-btn ${searchClass} ${config.button.roundedDefault ? 'roundedDefault' : ''}" type="button" style="width: 100%; border-style: none; padding: 0.5rem 2rem; ${buttonStyles.join(';')}">
        ${icon}
        ${textButton}
      </button>
    </div>`;

    return `
    <form id="cc-${elementId}-form" method="POST" action="" style="border-radius: 1rem;${blockStyles.join(';')}">
      <fieldset>
        <legend class="cc-sr-only">${getMessage(config.lang, 'searchOffers.form.legend')}</legend>
        <div style="margin-bottom: 10px;">
          ${job}
          ${location}
          ${contractType}
          ${searchBtn}
        </div>
      </fieldset>
    </form>
  `;

  }
}

function updateAnnouncement(element: HTMLElement, quantity: number, config: CcSearchOffersConfig): void {
  const suffix = ' - ' + getMessage(config.lang, 'searchOffers.autocompleteAnnouncements.hint');
  if (quantity > 1) {
    element.textContent = getMessage(config.lang, 'searchOffers.autocompleteAnnouncements.many', quantity.toString()) + suffix;
  } else if (quantity === 1) {
    element.textContent = getMessage(config.lang, 'searchOffers.autocompleteAnnouncements.one') + suffix;
  } else if (quantity === 0) {
    element.textContent = getMessage(config.lang, 'searchOffers.autocompleteAnnouncements.empty');
  } else {
    element.textContent = '';
  }
}

function resetAnnouncementOnEmpty(input: HTMLInputElement, elem: HTMLElement | null, config: CcSearchOffersConfig): void {
  if (!elem) return;
  input.addEventListener('input', () => {
    if (!input.value) updateAnnouncement(elem, -1, config);
  });
}

function enhanceContractTypeA11y(ssMain: Element | null, config: CcSearchOffersConfig): void {
  if (!ssMain) return;

  const deleteButtons = ssMain.querySelectorAll('.ss-value-delete');
  deleteButtons.forEach(deleteButton => {
    const label = deleteButton.parentElement?.querySelector('.ss-value-text')?.textContent || '';
    deleteButton.setAttribute('role', 'button');
    deleteButton.setAttribute('aria-label', getMessage(config.lang, 'searchOffers.contractType.removeItem', label));

    if (!deleteButton.getAttribute('data-cc-a11y')) {
      deleteButton.setAttribute('data-cc-a11y', '1');
      deleteButton.addEventListener('keydown', (e: Event) => {
        const key = (e as KeyboardEvent).key;
        if (key !== 'Enter' && key !== ' ' && key !== 'Spacebar') {
          return;
        }
        e.stopPropagation();
        if (key !== 'Enter') {
          e.preventDefault();
          (deleteButton as HTMLElement).click();
        }
        (ssMain as HTMLElement).focus();
      });
    }
  });

  const clearAll = ssMain.querySelector('.ss-deselect');
  if (clearAll) {
    clearAll.setAttribute('role', 'button');
    clearAll.setAttribute('tabindex', '0');
    clearAll.setAttribute('aria-label', getMessage(config.lang, 'searchOffers.contractType.clearAll'));

    if (!clearAll.getAttribute('data-cc-a11y')) {
      clearAll.setAttribute('data-cc-a11y', '1');
      clearAll.addEventListener('keydown', (e: Event) => {
        const key = (e as KeyboardEvent).key;
        if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
          e.preventDefault();
          e.stopPropagation();
          (clearAll as HTMLElement).click();
          (ssMain as HTMLElement).focus();
        }
      });
    }
  }
}

function announceContractTypeChange(elem: HTMLElement | null, previous: Option[], current: Option[], config: CcSearchOffersConfig): void {
  if (!elem) return;
  const previousValues = previous.map(o => o.value);
  const currentValues = current.map(o => o.value);
  const added = current.filter(o => previousValues.indexOf(o.value) === -1);
  const removed = previous.filter(o => currentValues.indexOf(o.value) === -1);

  if (added.length) {
    elem.textContent = getMessage(config.lang, 'searchOffers.contractType.added', added[0].text);
  } else if (removed.length === 1) {
    elem.textContent = getMessage(config.lang, 'searchOffers.contractType.removed', removed[0].text);
  } else if (removed.length > 1) {
    elem.textContent = getMessage(config.lang, 'searchOffers.contractType.cleared');
  }
}

export function searchOffers(container: string | HTMLElement, config: CcSearchOffersConfig = ccSearchOffersDefaultConfig) {
  const lang = getDefaultLang();
  config = {
    lang: config.lang ? config.lang : lang,
    domain: config.domain ? config.domain : ccSearchOffersDefaultConfig.domain,
    newTab: config.newTab ? config.newTab : ccSearchOffersDefaultConfig.newTab,
    job: {
      display: typeof config.job?.display === 'boolean' && config.job.display !== ccSearchOffersDefaultConfig.job.display ? config.job.display : ccSearchOffersDefaultConfig.job.display,
      disableAutocomplete: typeof config.job?.disableAutocomplete === 'boolean' && config.job.disableAutocomplete !== ccSearchOffersDefaultConfig.job.disableAutocomplete ? config.job.disableAutocomplete : ccSearchOffersDefaultConfig.job.disableAutocomplete,
      defaultValue: config.job?.defaultValue ? config.job.defaultValue : ccSearchOffersDefaultConfig.job.defaultValue,
      placeholder: config.job?.placeholder ? config.job.placeholder : getMessage(config.lang ? config.lang : lang, 'searchOffers.job.placeholder'),
      classes: config.job?.classes ? config.job.classes : ccSearchOffersDefaultConfig.job.classes,
      title: config.job?.title ? config.job.title : ccSearchOffersDefaultConfig.job.title,
      roundedDefault: typeof config.job?.roundedDefault === 'boolean' && config.job.roundedDefault !== ccSearchOffersDefaultConfig.job.roundedDefault ? config.job.roundedDefault : ccSearchOffersDefaultConfig.job.roundedDefault,
    },
    location: {
      display: typeof config.location?.display === 'boolean' && config.location.display !== ccSearchOffersDefaultConfig.location.display ? config.location.display : ccSearchOffersDefaultConfig.location.display,
      searchByGeonameId: typeof config.location?.searchByGeonameId === 'boolean' && config.location.searchByGeonameId !== ccSearchOffersDefaultConfig.location.searchByGeonameId ? config.location.searchByGeonameId : ccSearchOffersDefaultConfig.location.searchByGeonameId,
      defaultValue: config.location?.defaultValue ? config.location.defaultValue : ccSearchOffersDefaultConfig.location.defaultValue,
      placeholder: config.location?.placeholder ? config.location.placeholder : getMessage(config.lang ? config.lang : lang, 'searchOffers.location.placeholder'),
      classes: config.location?.classes ? config.location.classes : ccSearchOffersDefaultConfig.location.classes,
      title: config.location?.title ? config.location.title : ccSearchOffersDefaultConfig.location.title,
      roundedDefault: typeof config.location?.roundedDefault === 'boolean' && config.location.roundedDefault !== ccSearchOffersDefaultConfig.location.roundedDefault ? config.location.roundedDefault : ccSearchOffersDefaultConfig.location.roundedDefault,
    },
    contractType: {
      display: typeof config.contractType?.display === 'boolean' && config.contractType.display !== ccSearchOffersDefaultConfig.contractType.display ? config.contractType.display : ccSearchOffersDefaultConfig.contractType.display,
      placeholder: config.contractType?.placeholder ? config.contractType.placeholder : getMessage(config.lang ? config.lang : lang, 'searchOffers.contractType.placeholder'),
      classes: config.contractType?.classes ? config.contractType.classes : ccSearchOffersDefaultConfig.contractType.classes,
      title: config.contractType?.title ? config.contractType.title : ccSearchOffersDefaultConfig.contractType.title,
      roundedDefault: typeof config.contractType?.roundedDefault === 'boolean' && config.contractType.roundedDefault !== ccSearchOffersDefaultConfig.contractType.roundedDefault ? config.contractType.roundedDefault : ccSearchOffersDefaultConfig.contractType.roundedDefault,
      noResult: config.contractType?.noResult ? config.contractType.noResult : getMessage(config.lang ? config.lang : lang, 'searchOffers.contractType.noResult'),
    },
    industries: typeof config.industries === 'object' && config.industries?.length ? config.industries : ccSearchOffersDefaultConfig.industries,
    button: {
      displayIcon: typeof config.button?.displayIcon === 'boolean' && config.button.displayIcon !== ccSearchOffersDefaultConfig.button.displayIcon ? config.button.displayIcon : ccSearchOffersDefaultConfig.button.displayIcon,
      displayText: typeof config.button?.displayText === 'boolean' && config.button.displayText !== ccSearchOffersDefaultConfig.button.displayText ? config.button.displayText : ccSearchOffersDefaultConfig.button.displayText,
      text: config.button?.text ? config.button.text : getMessage(config.lang ? config.lang : lang, 'searchOffers.button.text'),
      color: config.button?.color ? config.button.color : ccSearchOffersDefaultConfig.button.color,
      bgColor: config.button?.bgColor ? config.button.bgColor : ccSearchOffersDefaultConfig.button.bgColor,
      classes: config.button?.classes ? config.button.classes : ccSearchOffersDefaultConfig.button.classes,
      roundedDefault: typeof config.button?.roundedDefault === 'boolean' && config.button.roundedDefault !== ccSearchOffersDefaultConfig.button.roundedDefault ? config.button.roundedDefault : ccSearchOffersDefaultConfig.button.roundedDefault,
    },
    block: {
      font: config.block?.font ? config.block.font : ccSearchOffersDefaultConfig.block.font,
      color: config.block?.color ? config.block.color : ccSearchOffersDefaultConfig.block.color,
      displayStyle: config.block?.displayStyle ? config.block.displayStyle : ccSearchOffersDefaultConfig.block.displayStyle,
    },
  };

  domReady(function () {
    queue.enqueue(async () => {
      if (container) {
        const element = typeof container === 'string' ? document.getElementById(container) : container;
        if (element) {
          element.innerHTML = display(element.id, config);

          const jobInput = <HTMLInputElement>document.getElementsByClassName('cc-input-jobs')[0];
          if (jobInput) {
            jobInput.value = config.job.defaultValue;
          }
          const locationInput = <HTMLInputElement>document.getElementsByClassName('cc-input-locations')[0];
          if (locationInput) {
            locationInput.value = config.location.defaultValue;
          }
          let locationInputGeonameId: string;
          let locationInputWhere: string;

          const buttonSubmit = <HTMLButtonElement>document.getElementsByClassName('cc-search-btn')[0];
          let displaySelect: null | SlimSelect = null;

          if (config.job.display) {
            if (!config.job.disableAutocomplete) {
              const jobAnnouncementElem = document.getElementById(`cc-job-announcement`);
              resetAnnouncementOnEmpty(jobInput, jobAnnouncementElem, config);

              autocomplete({
                input: jobInput,
                fetch: async (text, update) => {
                  text = text.toLowerCase();
                  const response = await fetch(`${config.domain}/api/jobs/search?query=${text}`, { credentials: 'include' });
                  const suggestions: AutocompleteItem[] = await response.json();
                  jobAnnouncementElem && updateAnnouncement(jobAnnouncementElem, suggestions.length, config);
                  update(suggestions);
                },
                onSelect: (item: AutocompleteItem) => {
                  jobInput.value = item.label || '';
                },
                disableAutoSelect: true,
                debounceWaitMs: 150,
              });
            }

            jobInput.addEventListener('change', () => {
              if (isSearchBtnEnabled(jobInput, locationInput, displaySelect, config)) {
                buttonSubmit.removeAttribute('disabled');
                buttonSubmit.style.cursor = 'pointer';
              } else {
                buttonSubmit.setAttribute('disabled', 'disabled');
                buttonSubmit.style.cursor = 'not-allowed';
              }
            });
          }

          if (config.location.display) {
            const locationAnnouncementElem = document.getElementById('cc-location-announcement');
            resetAnnouncementOnEmpty(locationInput, locationAnnouncementElem, config);

            autocomplete<LocationAutocompleteItem>({
              input: locationInput,
              fetch: async (text, update) => {
                text = text.toLowerCase();
                const response = await fetch(`${config.domain}/api/autocomplete/location?query=${text}`, { credentials: 'include' });
                const suggestions: Suggestions = await response.json();
                const data: LocationAutocompleteItem[] = suggestions.interpretations.map(interpretation => ({
                  name: interpretation.feature.name,
                  label: interpretation.feature.highlightedName,
                  preferredLabel: getPreferredLabel(interpretation),
                  value: interpretation.feature.id.split(':')[1],
                }));
                locationAnnouncementElem && updateAnnouncement(locationAnnouncementElem, data.length, config);
                update(data);
              },
              render: (item: LocationAutocompleteItem) => {
                const itemElement = document.createElement('div');
                itemElement.innerHTML = item.label || '';
                return itemElement;
              },
              onSelect: (item: LocationAutocompleteItem) => {
                locationInput.value = item.name || '';
                locationInputWhere = item.preferredLabel;
                if (config.location.searchByGeonameId) {
                  locationInputGeonameId = item.value;
                }
              },
              disableAutoSelect: true,
              debounceWaitMs: 150,
            });

            locationInput.addEventListener('change', () => {
              if (isSearchBtnEnabled(jobInput, locationInput, displaySelect, config)) {
                buttonSubmit.removeAttribute('disabled');
                buttonSubmit.style.cursor = 'pointer';
              } else {
                buttonSubmit.setAttribute('disabled', 'disabled');
                buttonSubmit.style.cursor = 'not-allowed';
              }
            });
          }

          if (config.contractType.display) {
            const contractAnnouncementElem = document.getElementById('cc-contract-type-announcement');
            let previousContractSelection: Option[] = [];

            displaySelect = new SlimSelect({
              select: '.cc-input-contract-types',
              settings: {
                showSearch: false,
                closeOnSelect: false,
                allowDeselect: true,
                ariaLabel: config.contractType.title,
                searchPlaceholder: config.contractType.placeholder,
                searchText: config.contractType.noResult,
                placeholderText: config.contractType.placeholder,
              },
              events: {
                afterChange: (info: Option[]) => {
                  const selection = info;
                  announceContractTypeChange(contractAnnouncementElem, previousContractSelection, selection, config);
                  previousContractSelection = selection;

                  const multiSelected = document.querySelector('.cc-input-contract-types + .ss-main, .cc-input-contract-types ~ .ss-main');
                  enhanceContractTypeA11y(multiSelected, config);

                  if (jobInput?.value || locationInput?.value || (Array.isArray(info) && info.length) ||
                    config.job.defaultValue || config.location.defaultValue) {
                    buttonSubmit.removeAttribute('disabled');
                    buttonSubmit.style.cursor = 'pointer';
                  } else {
                    buttonSubmit.setAttribute('disabled', 'disabled');
                    buttonSubmit.style.cursor = 'not-allowed';
                  }
                },
              },
            });

            await fetch(`${config.domain}/api/contract-types`, { credentials: 'include' })
              .then(response => response.json())
              .then(json => {
                for (let i = 0; i < json.length; i++) {
                  displaySelect?.addOption({ value: json[i].meteojobId, text: json[i].value });
                }
              });

            const observer = new MutationObserver(() => {
              const multiSelected = document.querySelector('.cc-input-contract-types + .ss-main, .cc-input-contract-types ~ .ss-main');

              const className = config.contractType?.classes || ccSearchOffersDefaultConfig.contractType.classes;

              if (multiSelected && className && !multiSelected.classList.contains(className)) {
                multiSelected.classList.add(className);
              }

              enhanceContractTypeA11y(multiSelected, config);
            });

            observer.observe(document.body, { childList: true, subtree: true });

            setTimeout(() => {
              const multiSelected = document.querySelector('.cc-input-contract-types + .ss-main, .cc-input-contract-types ~ .ss-main');
              const className = config.contractType?.classes || ccSearchOffersDefaultConfig.contractType.classes;

              if (multiSelected && className) {
                multiSelected.classList.add(className);
              }

              enhanceContractTypeA11y(multiSelected, config);
            }, 500);
          }

          if (!isSearchBtnEnabled(jobInput, locationInput, displaySelect, config)) {
            buttonSubmit.setAttribute('disabled', 'disabled');
            buttonSubmit.style.cursor = 'not-allowed';
          } else {
            buttonSubmit.removeAttribute('disabled');
            buttonSubmit.style.cursor = 'pointer';
          }

          buttonSubmit.addEventListener('click', () => {
            const query: string[] = [];
            if (jobInput?.value) {
              query.push(`what=${jobInput.value}`);
            } else if (config.job.defaultValue) {
              query.push(`what=${config.job.defaultValue}`);
            }

            if (locationInput?.value) {
              if (config.location.searchByGeonameId) {
                query.push(`location=${locationInputGeonameId}`);
              } else {
                query.push(`where=${locationInputWhere || locationInput.value}`);
              }
            } else if (config.location.defaultValue) {
              query.push(`where=${config.location.defaultValue}`);
            }

            if (displaySelect?.getSelected().length) {
              const ct = <string[]>displaySelect?.getSelected();
              query.push(`contracts=${ct.join(',')}`);
            }

            if (config.industries.length) {
              config.industries.forEach(industry => query.push(`industry=${industry}`));
            }

            window.open(`${config.domain}/jobs?${query.join('&')}`, config.newTab ? '_blank' : '_self');
          });
        }
      }
    });
  });
}
